import React from 'react';
import classNames from 'classnames';
import Box from '../Box';
import styles from './colorSwatch.module.scss';

export interface ColorSwatchType {
  /**
   * Color name
   */
  label: string;
  /**
   * Color hex value, e.g., "#F2D07E"
   */
  hex: string;
  /**
   * Image URL for the color swatch
   */
  url?: string | null;
  /**
   * Whether or not the sku for the color swatch is out of stock.
   * Color Swatch component will render with a crossed-out style.
   */
  isOutOfStock?: boolean;
  /**
   * Displays a selected treatment on the ColorSwatch
   */
  isSelected?: boolean;
  /**
   * Size of interactive color swatches. `small` is intended for `ProductCard`s, `large` is for the
   * Product-details page (PDP).
   * @default 'large'
   */
  size?: 'small' | 'large';
  /**
   * Use case of the ColorSwatch. Interactive swatches have interactive styling applied.
   * @default 'static'
   */
  type?: 'static' | 'interactive';
}

/**
 * A single color swatch.
 */
const ColorSwatch = ({
  label,
  hex,
  url,
  isOutOfStock,
  isSelected,
  size = 'large',
  type = 'static',
}: ColorSwatchType): JSX.Element => {
  const inputLabel = [isOutOfStock && 'Out of stock:', label, url && 'pattern']
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      className={classNames(styles['color-swatch'], styles[`size-${size}`], {
        [styles['is-interactive']]: type === 'interactive',
        [styles['is-static']]: type === 'static',
        [styles['is-selected']]: isSelected,
        [styles['is-out-of-stock']]: isOutOfStock,
      })}
      data-testid="color-swatch"
    >
      <span
        aria-label={inputLabel}
        className={styles.color}
        style={{ backgroundColor: hex }}
        role="img"
      >
        {url && (
          <img
            role="presentation"
            alt=""
            src={url}
            data-testid="color-swatch-image"
          />
        )}
      </span>
    </Box>
  );
};

export default ColorSwatch;
